import React, { FunctionComponent } from 'react';
import { ResponsiveBar } from '@nivo/bar';

type DefaultStackedBarChartProps = {
  data: { [key: string]: any }[];
  keys: string[];
  indexBy: string;
  colors?: string[];
  showPercentages?: boolean;
};

const DefaultStackedBarChart: FunctionComponent<
  DefaultStackedBarChartProps
> = ({ data, keys, indexBy, colors, showPercentages = false }) => {
  const totalsPerMonth = data.reduce((acc, item) => {
    const monthName = item[indexBy];
    acc[monthName] = keys.reduce(
      (sum, key) => sum + (Number(item[key]) || 0),
      0,
    );
    return acc;
  }, {});

  // If showPercentages is true, convert all data to percentage values
  const percentageData = showPercentages
    ? data.map((item) => {
        const total = totalsPerMonth[item[indexBy]];
        if (!total) return item;

        const newItem = { ...item };
        keys.forEach((key) => {
          newItem[key] = (item[key] / total) * 100;
        });
        return newItem;
      })
    : data;

  return (
    <div style={{ height: 500, textAlign: 'center' }}>
      <ResponsiveBar
        data={showPercentages ? percentageData : data}
        keys={keys}
        indexBy={indexBy}
        colors={colors ?? { scheme: 'nivo' }}
        margin={{ top: 30, right: 30, bottom: 60, left: 50 }}
        padding={0.3}
        tooltip={({ id, value, color, indexValue }) => {
          const originalValue = showPercentages
            ? data.find((item) => item[indexBy] === indexValue)?.[id]
            : value;

          const percentage = (
            (originalValue / totalsPerMonth[indexValue as string]) *
            100
          ).toFixed(1);

          return (
            <div
              style={{
                padding: '9px 12px',
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: '3px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              <strong>
                {indexValue}: {id}
              </strong>
              <div>
                <span
                  style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    background: color,
                    marginRight: '7px',
                  }}
                ></span>
                <strong>{`${percentage}%`}</strong>
                {showPercentages ? ` (${originalValue})` : ` (${value})`}
              </div>
            </div>
          );
        }}
        valueFormat={
          showPercentages ? (value) => `${Math.round(value)}%` : undefined
        }
        valueScale={{
          type: 'linear',
          min: 0,
          max: showPercentages ? 100 : 'auto',
        }}
        indexScale={{ type: 'band', round: true }}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickPadding: 12,
          tickRotation: 45,
          format: (value) => value,
        }}
        axisLeft={{
          tickPadding: 12,
          format: showPercentages
            ? (value) => `${Math.round(value)}%`
            : undefined,
        }}
        enableGridY={true}
        labelSkipWidth={12}
        labelSkipHeight={12}
        legends={[
          {
            anchor: 'top-left',
            direction: 'row',
            translateX: -37,
            translateY: -35,
            itemWidth: 130,
            itemHeight: 20,
            symbolSize: 12,
            symbolShape: 'square',
            dataFrom: 'keys',
          },
        ]}
      />
    </div>
  );
};

export default DefaultStackedBarChart;
